import React from 'react';
import styled from 'styled-components';
import { Color, Grid, Type } from '../../app/StyleGuide';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const formatDate = (date: number): string => new Date(date).toLocaleDateString();

const pluralize = (text: string, count: number): string => (count === 1 ? text : text + 's');

const capitalize = (text: string): string => text.replace(/(^|\s)\S/g, t => t.toUpperCase());

// ---------------------------------------------------------------------------
// Metadata
// ---------------------------------------------------------------------------

const StyledMetadataList = styled.ul`
  margin: ${Grid._2} 0 ${Grid._4} 0;
  padding: 0;
  list-style: none;
`;

const StyledMetadataItem = styled.li`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
  display: inline-block;

  &:not(:first-of-type)::before {
    content: '\u2022';
    margin: 0 ${Grid._2};
  }
`;

const PathwayBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${Grid._5};
  width: ${Grid._5};
  font-size: ${Type.Scale._2};
  border-radius: 50%;
  background-color: ${Color.Blue._50};
  color: white;
`;

type MetadataProps = {
  type: 'course' | 'pathway';
  stepsCompleted?: number;
  stepsTotal?: number;
  stepLabel?: string;
  completedOn?: number;
  dueDate?: number;
  pending?: boolean;
};

export function Metadata({
  type,
  stepsCompleted,
  stepsTotal,
  stepLabel,
  completedOn,
  dueDate,
  pending,
}: MetadataProps) {
  const typeLabel = capitalize(type);
  const hasSteps = stepsCompleted !== undefined && stepsTotal !== undefined;
  const formattedStepLabel = stepLabel && hasSteps ? pluralize(stepLabel, stepsTotal) : stepLabel;

  return (
    <StyledMetadataList>
      {pending ? (
        <StyledMetadataItem>Will be assigned upon login</StyledMetadataItem>
      ) : (
        <>
          <StyledMetadataItem>
            {type === 'pathway' && (
              <PathwayBadge>
                <i className="fas fa-route" />
              </PathwayBadge>
            )}{' '}
            {typeLabel}
          </StyledMetadataItem>
          {completedOn ? (
            <StyledMetadataItem>Completed on {formatDate(completedOn)}</StyledMetadataItem>
          ) : (
            hasSteps && (
              <StyledMetadataItem>
                {stepsCompleted} of {stepsTotal} {formattedStepLabel} complete
              </StyledMetadataItem>
            )
          )}
          {dueDate && <StyledMetadataItem>Finish by {formatDate(dueDate)}</StyledMetadataItem>}
        </>
      )}
    </StyledMetadataList>
  );
}

// ---------------------------------------------------------------------------
// Details
// ---------------------------------------------------------------------------

const StyledDetailsList = styled.ul`
  margin: 0 ${Grid._4} ${Grid._5} 0;
  padding: 0;
  list-style: none;
`;

type DetailsProps = {
  assignor?: string | null;
  selfAssigned?: boolean;
  shared?: boolean;
  sharedOn?: number;
  assignedOn?: number;
};

export function Details({ assignor, selfAssigned, shared, sharedOn, assignedOn }: DetailsProps) {
  const formattedSharedOn = sharedOn ? formatDate(sharedOn) : undefined;
  const formattedAssignedOn = assignedOn ? formatDate(assignedOn) : undefined;

  const hasAssignmentInfo = assignor || formattedAssignedOn;

  return (
    <StyledDetailsList>
      {shared ? (
        <li>Shared on {formattedSharedOn}</li>
      ) : (
        hasAssignmentInfo && (
          <li>
            {selfAssigned ? 'Self-assigned' : 'Assigned'}
            {!selfAssigned && assignor ? ` by ${assignor}` : ''}
            {formattedAssignedOn ? ` on ${formattedAssignedOn}` : ''}
          </li>
        )
      )}
    </StyledDetailsList>
  );
}
